/**
 * Ocean Booking Charges Fields Component
 *
 * Renders the charges table for carrier response data.
 * Each charge row contains: chargeName, currencyAmount, currencyCode,
 * paymentTermCode, calculationBasis, quantity, unitPrice.
 *
 * This is response data from the carrier and is typically read-only,
 * but supports edit mode for corrections.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * OBChargesFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function OBChargesFields({ control, register, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'charges'
  });

  const addCharge = () => {
    append({
      chargeName: '',
      currencyAmount: null,
      currencyCode: 'USD',
      paymentTermCode: '',
      calculationBasis: '',
      quantity: null,
      unitPrice: null
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Charges
        </h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addCharge}>
            + Add Charge
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No charges listed.
        </div>
      )}

      {/* Charges table */}
      {fields.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Charge Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Currency</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Payment Term</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Calc Basis</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                {isEditMode && (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td className="px-3 py-2">
                    <input
                      {...register(`charges.${index}.chargeName`)}
                      disabled={!isEditMode}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="Charge name"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      {...register(`charges.${index}.currencyAmount`, { valueAsNumber: true })}
                      disabled={!isEditMode}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      {...register(`charges.${index}.currencyCode`)}
                      disabled={!isEditMode}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="USD"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      {...register(`charges.${index}.paymentTermCode`)}
                      disabled={!isEditMode}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="PRE"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      {...register(`charges.${index}.calculationBasis`)}
                      disabled={!isEditMode}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                      placeholder="Per container"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      {...register(`charges.${index}.quantity`, { valueAsNumber: true })}
                      disabled={!isEditMode}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      step="0.01"
                      {...register(`charges.${index}.unitPrice`, { valueAsNumber: true })}
                      disabled={!isEditMode}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-600"
                    />
                  </td>
                  {isEditMode && (
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OBChargesFields;
